import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import type { ResolveFactorRequest, ResolveFactorResponse } from '../types';
import type { FactorSelectionMode } from '../types/activity-data';

import { useGHGOrganizationId } from './use-ghg-organization-id';

import { createApiClient } from '@/lib/api-client';

export const factorResolutionKeys = {
  all: ['factor-resolution'] as const,
  resolve: (orgId: string, request: ResolveFactorRequest | null) =>
    [...factorResolutionKeys.all, 'resolve', orgId, request] as const,
};

export function useResolveFactor(
  request: ResolveFactorRequest | null,
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: factorResolutionKeys.resolve(orgId, request),
    queryFn: async () => {
      if (!request) throw new Error('Factor resolution request is required');
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return apiClient.post<ResolveFactorResponse>(
        `/organizations/${orgId}/ghg/emission-factors/resolve`,
        request
      );
    },
    enabled:
      Boolean(orgId) &&
      Boolean(request?.activityId) &&
      Boolean(request?.unitType) &&
      (options?.enabled ?? true),
  });
}

export function buildFactorSelectionPayload(input: {
  selectedFactorId?: string;
  selectedSelectorSource?: 'climatiq' | 'custom';
  selectionMode?: FactorSelectionMode;
  selector?: Record<string, unknown>;
  resolution?: Record<string, unknown>;
}) {
  if (!input.selectedFactorId || !input.selectionMode) {
    return {
      emissionFactorId: undefined,
      emissionFactorSelectorSource: undefined,
      factorSelectionMode: undefined,
      emissionFactorParameters: undefined,
    };
  }
  return {
    emissionFactorId: input.selectedFactorId,
    emissionFactorSelectorSource: input.selectedSelectorSource,
    factorSelectionMode: input.selectionMode,
    emissionFactorParameters: {
      ...(input.selector ?? {}),
      ...(input.resolution ?? {}),
    },
  };
}
